/**
 * Disputes service — Firestore access for the `disputes` collection.
 *
 * A dispute is a mentor-raised issue report attached to ONE class (doc id ===
 * classId, same keying as sessionLogs — so a class carries at most one
 * dispute). Mentors raise it from their Session log when something went wrong
 * around a session (join/link trouble, student never showed, technical
 * problems, …); admins triage it from the dedicated Disputes page.
 *
 * A dispute document:
 *   {
 *     classId,                              // === doc id (enforced in rules)
 *     mentorId, mentorEmail,                // identity (denormalized for rules)
 *     mentorName, studentId, studentName,   // display snapshots
 *     classTitle, subject,
 *     scheduledStartAt: Timestamp,          // = class.startAt snapshot
 *     category,                             // see DISPUTE_CATEGORIES
 *     description,                          // the mentor's written report
 *     status: 'open'|'in_review'|'resolved'|'dismissed',
 *     adminResponse,                        // admin's resolution note
 *     resolvedAt: Timestamp|null, resolvedBy,
 *     createdAt, updatedAt
 *   }
 *
 * Lifecycle: the mentor may rewrite category/description while the dispute is
 * still 'open'; once an admin picks it up (any status change) it becomes
 * read-only for the mentor. Admins own status + adminResponse.
 */
import firestoreService, { where, serverTimestamp, Timestamp } from './firestore.service';

const COLLECTION = 'disputes';

export const DISPUTE_STATUS = Object.freeze({
  OPEN: 'open',
  IN_REVIEW: 'in_review',
  RESOLVED: 'resolved',
  DISMISSED: 'dismissed',
});

export const DISPUTE_STATUS_LABEL = Object.freeze({
  [DISPUTE_STATUS.OPEN]: 'Open',
  [DISPUTE_STATUS.IN_REVIEW]: 'In review',
  [DISPUTE_STATUS.RESOLVED]: 'Resolved',
  [DISPUTE_STATUS.DISMISSED]: 'Dismissed',
});

/** Badge variants keyed by status — single source for every surface. */
export const DISPUTE_STATUS_VARIANT = Object.freeze({
  [DISPUTE_STATUS.OPEN]: 'danger',
  [DISPUTE_STATUS.IN_REVIEW]: 'gold',
  [DISPUTE_STATUS.RESOLVED]: 'success',
  [DISPUTE_STATUS.DISMISSED]: 'neutral',
});

/** What kind of problem the mentor is reporting. */
export const DISPUTE_CATEGORIES = Object.freeze([
  { value: 'join_issue', label: 'Join / meeting link issue' },
  { value: 'student_no_show', label: 'Student did not join' },
  { value: 'technical', label: 'Technical problem during class' },
  { value: 'schedule_mismatch', label: 'Timing / schedule mismatch' },
  { value: 'other', label: 'Other' },
]);

export const DISPUTE_CATEGORY_LABEL = Object.freeze(
  Object.fromEntries(DISPUTE_CATEGORIES.map((c) => [c.value, c.label]))
);

/** Coerce a Timestamp | Date | ms | ISO into a Firestore Timestamp (or null). */
function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === 'function') return value; // already Timestamp-like
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

export const disputesService = {
  /** Admin: every dispute platform-wide (sorted client-side). */
  subscribeAll(onData, onError) {
    return firestoreService.subscribeCollection(COLLECTION, [], onData, onError);
  },

  // `subscribe` alias so the generic useCollection hook can consume this service.
  subscribe(onData, onError) {
    return this.subscribeAll(onData, onError);
  },

  /** One mentor's disputes — query-provable via the denormalized mentorId. */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('mentorId', '==', mentorId)],
      onData,
      onError
    );
  },

  get(classId) {
    return firestoreService.getDocument(COLLECTION, classId);
  },

  /**
   * Raise a dispute for a class. Doc id === classId, so raising twice for the
   * same class is rejected by rules once the first exists (the UI swaps the
   * Raise button for the status badge anyway).
   *
   * @param {object} cls     the class row ({ id, title, subject, startAt, studentId })
   * @param {object} fields  { category, description }
   * @param {object} mentor  the resolved mentors/{id} record of the caller
   * @param {string} studentName  display snapshot for the admin table
   */
  raise(cls, fields, mentor, studentName) {
    return firestoreService.setDocument(COLLECTION, cls.id, {
      classId: cls.id,
      mentorId: mentor.id,
      mentorEmail: (mentor.email || '').toLowerCase(),
      mentorName: mentor.name || '',
      studentId: cls.studentId || '',
      studentName: studentName || '',
      classTitle: cls.title || '',
      subject: cls.subject || '',
      scheduledStartAt: toTimestamp(cls.startAt),
      category: fields.category || 'other',
      description: (fields.description || '').trim(),
      status: DISPUTE_STATUS.OPEN,
      adminResponse: '',
      resolvedAt: null,
      resolvedBy: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  /** Mentor edit of their own still-open dispute (rules whitelist these keys). */
  amend(classId, fields) {
    return firestoreService.updateDocument(COLLECTION, classId, {
      category: fields.category || 'other',
      description: (fields.description || '').trim(),
    });
  },

  /**
   * Admin triage: change status and/or record a response. Moving into a
   * terminal state stamps who resolved it and when; reopening clears both.
   *
   * @param {string} classId
   * @param {object} patch   { status, adminResponse }
   * @param {object} [actor] { name } for the resolvedBy stamp
   */
  adminUpdate(classId, patch, actor = {}) {
    const data = {};
    if ('status' in patch) {
      data.status = patch.status;
      const terminal =
        patch.status === DISPUTE_STATUS.RESOLVED || patch.status === DISPUTE_STATUS.DISMISSED;
      data.resolvedAt = terminal ? serverTimestamp() : null;
      data.resolvedBy = terminal ? actor.name || 'admin' : '';
    }
    if ('adminResponse' in patch) data.adminResponse = (patch.adminResponse || '').trim();
    return firestoreService.updateDocument(COLLECTION, classId, data);
  },

  remove(classId) {
    return firestoreService.deleteDocument(COLLECTION, classId);
  },
};

export default disputesService;
